// WHY — status-led dashboard. Health banner first, multi-line CPL overlay
// chart second, sortable/filterable account table third. Range (30/60/90)
// lives in the URL so it's shareable and survives reload. All hooks run
// before any conditional rendering to satisfy Rules of Hooks.

import { useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Banner } from "../components/Banner";
import { Card } from "../components/Card";
import { AlertBanner } from "../components/AlertBanner";
import { SmartOverlayChart, type ChartAccount } from "../components/SmartOverlayChart";
import { AccountTable, type TableAccount } from "../components/AccountTable";
import { RangeToggle } from "../components/RangeToggle";
import { api } from "../lib/api";
import { rollingAverage } from "../lib/smoothing";
import type { AccountStatus, Range } from "../lib/types";

const SMOOTHING_WINDOW = 7;

interface DashboardAccount {
  account_id: string;
  display_name: string;
  currency: string;
  timezone: string;
  status: AccountStatus;
  spend_7d: number;
  leads_7d: number;
  cpl_7d: number | null;
  cpl_delta_pct: number | null;
  sparkline: Array<{ date: string; cpl: number | null }>;
}

interface DashboardResponse {
  client: { client_id: string };
  alerts_summary: {
    active: number;
    flagged_account_ids: string[];
    last_checked_at: string | null;
  };
  accounts: DashboardAccount[];
}

function readRangeFromURL(): Range {
  if (typeof window === "undefined") return 90;
  const raw = new URLSearchParams(window.location.search).get("range");
  if (raw === "30") return 30;
  if (raw === "60") return 60;
  if (raw === "90") return 90;
  return 90;
}

function writeRangeToURL(range: Range): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("range", String(range));
  window.history.replaceState(null, "", url.toString());
}

export function DashboardPage() {
  const [range, setRange] = useState<Range>(() => readRangeFromURL());
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(() => new Set());
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    writeRangeToURL(range);
    setData(null);
    setErr(null);
    let cancelled = false;
    api<DashboardResponse>(`/api/dashboard?range=${range}`)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load";
        setErr(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  // Smooth each account's daily CPL with a trailing 7-day rolling average.
  // Daily CPL is noisy at the per-account level (one bad day can spike
  // a small account). Smoothing makes trend direction easier to see at
  // a glance without changing the underlying numbers reported elsewhere.
  const chartAccounts = useMemo<ChartAccount[]>(() => {
    if (!data) return [];
    return data.accounts.map((a) => {
      const smoothed = rollingAverage(
        a.sparkline.map((p) => p.cpl),
        SMOOTHING_WINDOW,
      );
      return {
        account_id: a.account_id,
        display_name: a.display_name,
        flagged: a.status === "flagged" || a.status === "warning",
        sparkline: a.sparkline.map((p, i) => ({ date: p.date, cpl: smoothed[i] ?? null })),
      };
    });
  }, [data]);

  const tableAccounts = useMemo<TableAccount[]>(() => {
    if (!data) return [];
    return data.accounts.map((a) => ({
      account_id: a.account_id,
      display_name: a.display_name,
      currency: a.currency,
      status: a.status,
      spend_7d: a.spend_7d,
      leads_7d: a.leads_7d,
      cpl_7d: a.cpl_7d,
      cpl_delta_pct: a.cpl_delta_pct,
      sparkline: a.sparkline,
    }));
  }, [data]);

  const flaggedSummary = useMemo(() => {
    if (!data) return [];
    return data.accounts
      .filter((a) => a.status === "flagged" || a.status === "warning")
      .slice(0, 3)
      .map((a) => ({
        account_id: a.account_id,
        display_name: a.display_name,
        cpl_delta_pct: a.cpl_delta_pct,
      }));
  }, [data]);

  const toggleHighlight = (id: string) => {
    setHighlightedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Layout clientId={data?.client.client_id}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "var(--space-5)",
        }}
      >
        <div>
          <span className="label">Account Overview</span>
          <h1 style={{ marginTop: "6px" }}>Dashboard</h1>
        </div>
        <RangeToggle value={range} onChange={setRange} />
      </div>

      {err && <Banner tone="err">{err}</Banner>}
      {!data && !err && <Banner tone="info">Loading…</Banner>}

      {data && data.accounts.length === 0 && (
        <Card>
          <p>
            No ad accounts connected yet. <a href="/meta/start">Connect Meta</a> to get started.
          </p>
        </Card>
      )}

      {data && data.accounts.length > 0 && (
        <>
          <AlertBanner
            active={data.alerts_summary.active}
            last_checked_at={data.alerts_summary.last_checked_at}
            flagged={flaggedSummary}
          />
          <div style={{ marginBottom: "var(--space-4)" }}>
            <SmartOverlayChart
              accounts={chartAccounts}
              highlightedIds={highlightedIds}
              hoveredId={hoveredId}
            />
            <p
              style={{
                margin: "6px 0 0",
                fontSize: "var(--fs-xs)",
                fontFamily: "var(--font-mono)",
                color: "var(--color-bone-dim)",
                letterSpacing: "var(--letter-label)",
                textTransform: "uppercase",
              }}
            >
              CPL · 7-day rolling average
            </p>
          </div>
          <Card padding="none">
            <div style={{ padding: "var(--space-3)" }}>
              <AccountTable
                accounts={tableAccounts}
                highlightedIds={highlightedIds}
                onToggleHighlight={toggleHighlight}
                onHover={setHoveredId}
              />
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
}
